import { apiClient } from "./api";
import type {
  SalesCommission,
  CommissionPayout,
  CommissionSummary,
  MyCommissionsResponse,
  CommissionsByRepItem,
  BulkPayData,
  CommissionFilters,
} from "../types/commissions";

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export async function fetchCommissions(
  filters: CommissionFilters = {},
): Promise<PaginatedResponse<SalesCommission>> {
  const params = new URLSearchParams();
  if (filters.page) params.append("page", String(filters.page));
  if (filters.search) params.append("search", filters.search);
  if (filters.date_field) params.append("date_field", filters.date_field);
  if (filters.status) params.append("status", filters.status);
  if (filters.sales_rep) params.append("sales_rep", String(filters.sales_rep));
  if (filters.date_from) params.append("date_from", filters.date_from);
  if (filters.date_to) params.append("date_to", filters.date_to);

  const qs = params.toString();
  const response = await apiClient.get<PaginatedResponse<SalesCommission>>(
    `/api/sales-commissions/${qs ? `?${qs}` : ""}`,
  );
  return response.data;
}

export async function fetchCommissionSummary(
  filters: CommissionFilters = {},
): Promise<CommissionSummary> {
  const query = new URLSearchParams(
    Object.entries(filters)
      .filter(([k, v]) => k !== "page" && v !== undefined && v !== "")
      .map(([k, v]) => [k, String(v)]),
  );
  const response = await apiClient.get<CommissionSummary>(
    `/api/sales-commissions/summary/?${query}`,
  );
  return response.data;
}

export async function fetchMyCommissions(
  status?: string,
): Promise<MyCommissionsResponse> {
  const qs = status ? `?status=${status}` : "";
  const response = await apiClient.get<MyCommissionsResponse>(
    `/api/sales-commissions/my-commissions/${qs}`,
  );
  return response.data;
}

export async function fetchCommissionsByRep(): Promise<CommissionsByRepItem[]> {
  const response = await apiClient.get<CommissionsByRepItem[]>(
    "/api/sales-commissions/by-sales-rep/",
  );
  return response.data;
}

export async function bulkPayCommissions(
  data: BulkPayData,
): Promise<{ payouts: CommissionPayout[]; reused: boolean }> {
  const response = await apiClient.post<{
    payouts: CommissionPayout[];
    reused: boolean;
  }>("/api/sales-commissions/bulk-pay/", data);
  return response.data;
}

export async function holdCommission(
  id: number,
  reason: string,
): Promise<SalesCommission> {
  const response = await apiClient.post<SalesCommission>(
    `/api/sales-commissions/${id}/hold/`,
    { reason },
  );
  return response.data;
}

export async function releaseCommission(id: number): Promise<SalesCommission> {
  const response = await apiClient.post<SalesCommission>(
    `/api/sales-commissions/${id}/release/`,
    {},
  );
  return response.data;
}

export async function overrideCommission(
  id: number,
  amount: number,
  reason: string,
): Promise<SalesCommission> {
  const response = await apiClient.post<SalesCommission>(
    `/api/sales-commissions/${id}/override/`,
    { amount, reason },
  );
  return response.data;
}

export async function fetchPayouts(
  filters: Record<string, string | number> = {},
): Promise<PaginatedResponse<CommissionPayout>> {
  const query = new URLSearchParams(
    Object.entries(filters).map(([k, v]) => [k, String(v)]),
  );
  const response = await apiClient.get<PaginatedResponse<CommissionPayout>>(
    `/api/commission-payouts/?${query}`,
  );
  return response.data;
}
